import { MoreOutlined } from '@ant-design/icons'
import { Button, Dropdown, Menu } from 'antd'
import { ColumnFilterItem } from 'antd/lib/table/interface'
import React from 'react'

import { emptyCharacter } from '../../../components/EmptyChar'
import { renderCoupon, renderDate, renderPrincipal } from '../../../components/tables/table-column-renderers'
import { buildSearchFilterProps, sortDateColumn, sortNumericColumn } from '../../../components/tables/utils'
import { getInterestPaidCodes, InstrumentCode, InterestPaid } from '../../../models'
import { TradeTableItem } from '../models'

const shouldDisableUpdateTrade = (instrumentCode: InstrumentCode) => ['CASH', 'BOND', 'FRN'].includes(instrumentCode)

const shouldDisableUpdateCouponDates = (instrumentCode: InstrumentCode) =>
  ['CASH', 'BOND', 'FRN'].includes(instrumentCode)

const renderActionsMenu =
  ({
    onUpdateTrade,
    onDeleteTrade,
    onUpdateCouponDates,
  }: {
    onUpdateTrade: (trade: TradeTableItem) => void
    onDeleteTrade: (trade: TradeTableItem) => void
    onUpdateCouponDates: (tradeId: number) => void
  }) =>
  (_: any, trade: TradeTableItem) => {
    const menu = (
      <Menu>
        <Menu.Item
          key="updateTrade"
          onClick={() => onUpdateTrade(trade)}
          disabled={shouldDisableUpdateTrade(trade.instrumentCode)}
        >
          Update trade
        </Menu.Item>
        <Menu.Item
          key="updateCouponDates"
          onClick={() => onUpdateCouponDates(trade.id)}
          disabled={shouldDisableUpdateCouponDates(trade.instrumentCode)}
        >
          Update coupon dates
        </Menu.Item>
        <Menu.Item key="deleteTrade" onClick={() => onDeleteTrade(trade)}>
          Delete trade
        </Menu.Item>
      </Menu>
    )

    return (
      <Dropdown placement="bottomRight" overlay={menu}>
        <Button icon={<MoreOutlined />} />
      </Dropdown>
    )
  }

const renderMaturityDate = (date: Date, trade: TradeTableItem) => {
  if (!date || trade.instrumentCode === 'CASH') return emptyCharacter

  return renderDate(date)
}

const interestPaidCodes = getInterestPaidCodes()

const renderInterestsPaidCode = (interest: InterestPaid) => interestPaidCodes[interest]

const buildInterestPaidCodeFilters = () =>
  Object.keys(interestPaidCodes).map((interestPaidCode) => ({
    value: interestPaidCode,
    text: interestPaidCodes[interestPaidCode],
  }))

export const buildTradesTableColumns = ({
  onUpdateTrade,
  onDeleteTrade,
  onUpdateCouponDates,
  filters,
}: {
  onUpdateTrade: (trade: TradeTableItem) => void
  onDeleteTrade: (tradeId: TradeTableItem) => void
  onUpdateCouponDates: (tradeId: number) => void
  filters: {
    instrumentCodes: ColumnFilterItem[]
    issuerCodes: ColumnFilterItem[]
    allocationCodes: ColumnFilterItem[]
  }
}) => [
  {
    title: 'ID',
    dataIndex: 'id',
    sorter: sortNumericColumn({ dataIndex: 'id' }),
    ...buildSearchFilterProps({ dataIndex: 'id' }),
  },
  {
    title: 'Class',
    dataIndex: 'instrumentCode',
    filters: filters.instrumentCodes,
    onFilter: (instrumentCode: string, trade: TradeTableItem) => trade.instrumentCode === instrumentCode,
  },
  {
    title: 'Issuer',
    dataIndex: 'issuerCode',
    filters: filters.issuerCodes,
    onFilter: (issuerCode: string, trade: TradeTableItem) => trade.issuerCode === issuerCode,
  },
  {
    title: 'Principal $',
    dataIndex: 'principal',
    render: renderPrincipal,
    sorter: sortNumericColumn({ dataIndex: 'principal' }),
  },
  { title: 'Coupon %', dataIndex: 'coupon', render: renderCoupon, sorter: sortNumericColumn({ dataIndex: 'coupon' }) },
  {
    title: 'Accrued $',
    dataIndex: 'accruedInterest',
    render: renderPrincipal,
    sorter: sortNumericColumn({ dataIndex: 'accruedInterest' }),
  },
  {
    title: 'Allocation',
    dataIndex: 'allocationCode',
    filters: filters.allocationCodes,
    onFilter: (allocationCode: string, trade: TradeTableItem) => trade.allocationCode === allocationCode,
  },
  {
    title: 'Interest Paid',
    dataIndex: 'interestPaidCode',
    render: renderInterestsPaidCode,
    filters: buildInterestPaidCodeFilters(),
    onFilter: (interestPaidCode: InterestPaid, trade: TradeTableItem) => trade.interestPaidCode === interestPaidCode,
  },
  {
    title: 'Purchase',
    dataIndex: 'purchaseDate',
    render: renderDate,
    sorter: sortDateColumn({ dataIndex: 'purchaseDate' }),
  },
  {
    title: 'Maturity',
    dataIndex: 'maturityDate',
    render: renderMaturityDate,
    sorter: sortDateColumn({ dataIndex: 'maturityDate' }),
  },
  {
    title: 'Next coupon',
    dataIndex: 'nextCouponDate',
    render: renderDate,
    sorter: sortDateColumn({ dataIndex: 'nextCouponDate' }),
  },
  { title: '', render: renderActionsMenu({ onUpdateTrade, onDeleteTrade, onUpdateCouponDates }) },
]
